import styled from '@emotion/styled';
import { Prismic, PrismicContext } from '@landsbankinn/prismic';
import {
  device,
  Divider,
  mobileGutterMargin,
  typography,
  shouldForwardProp,
} from '@landsbankinn/ui';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import React, { useCallback, forwardRef, useContext, useState } from 'react';
import { animated } from 'react-spring';
import { ImageVideo } from '../../components/image-video/ImageVideo';
import type { ImgAr, ImgSource } from '../../components/img/Img';
import { Img } from '../../components/img/Img';

export const CardHostLink = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<Partial<CardProps>>`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: stretch;
  position: relative;
  width: auto;
  cursor: pointer;
  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};
  background-color: ${(props) =>
    props.backgroundAlt
      ? props.theme.colors.backgroundAlt
      : props.theme.colors.background};
  &:hover img:first-child {
    transform: scale(1.025);
  }
  ${mobileGutterMargin}
  @media ${device.tablet} {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`;

const CardHost = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<Partial<CardProps>>`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: stretch;
  position: relative;
  width: auto;
  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};
  background-color: ${(props) =>
    props.backgroundAlt
      ? props.theme.colors.backgroundAlt
      : props.theme.colors.background};
  ${mobileGutterMargin}
  @media ${device.tablet} {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`;

const Body = styled.div<{
  padding: boolean;
  moreBodySpace?: boolean;
  notLink?: boolean;
  text_in_image?: boolean;
}>`
  position: relative;
  flex: 1;
  padding: ${(props) =>
    props.notLink
      ? `${props.theme.containerGutter}px`
      : `36px ${props.theme.containerGutter}px`};
  @media ${device.mobile} {
    padding: ${(props) =>
      props.notLink
        ? `${props.theme.breakpoints.sm.gutter}px`
        : `36px ${props.theme.breakpoints.sm.gutter}px`};
    ${(props) => (props.text_in_image ? 'padding-top:0;' : '')};
  }
  @media ${device.tablet} {
    padding: ${(props) => (props.padding ? 36 : 0)}px;
    padding-top: ${(props) =>
      props.padding ? 36 : props.moreBodySpace ? 60 : 24}px;
    ${(props) => (props.text_in_image ? 'padding-top:0;' : '')};
  }
  ${(props) =>
    props.text_in_image
      ? 'position:absolute;color:white;top:50%;left:0;width:100%;padding-top: 0;margin-top: -20px;text-align: center;'
      : ''};
`;

export const Title = styled.h2<{
  hasMinHeight?: boolean;
  text_in_image?: boolean;
}>`
  ${typography.h3};
  margin-bottom: 3px;
  ${(props) =>
    props.text_in_image ? `font-family:${props.theme.font.family}` : ''};
  @media ${device.tablet} {
    ${(props) => props.hasMinHeight && 'min-height: 72px'};
  }
`;

const Copy = styled.div`
  ${typography.body}
  margin-bottom: 24px;
  @media ${device.tablet} {
    padding-right: 28px;
  }
`;

const ActionSpace = styled.div`
  position: relative;
  opacity: 0;
  pointer-events: none;
`;

const ActionPosition = styled.div<{ padding: boolean }>`
  display: flex;
  flex-direction: row;
  position: absolute;
  bottom: 36px;
  left: ${(props) => props.theme.breakpoints.sm.gutter}px;
  right: ${(props) => props.theme.breakpoints.sm.gutter}px;
  @media ${device.tablet} {
    bottom: ${(props) => (props.padding ? 36 : 0)}px;
    left: ${(props) => (props.padding ? 36 : 0)}px;
    right: ${(props) => (props.padding ? 36 : 0)}px;
  }
`;

interface CardActionProps {
  children: React.ReactElement;
  padding: boolean;
  hover?: boolean;
}

export const CardAction = ({
  children,
  padding,
  hover,
  ...rest
}: CardActionProps) => {
  return (
    <>
      <ActionSpace>{children}</ActionSpace>
      <ActionPosition padding={padding}>
        {React.cloneElement(children, {
          ...rest,
          hover,
        })}
      </ActionPosition>
    </>
  );
};

export interface CardProps {
  link?: Prismic.Link;
  title: string | React.ReactNode;
  description: React.ReactNode;
  image?: ImgSource;
  percentage?: number;
  ar?: ImgAr;
  imageAspect?: number;
  action?: React.ReactElement;
  padding?: boolean;
  backgroundAlt?: boolean;
  backgroundColor?: string;
  imageRef?: any;
  ref?: any;
  notLink?: boolean;
  moreBodySpace?: boolean;
  video?: Prismic.Embed;
  onMouseEnter?: any;
  onMouseLeave?: any;
  onClick?: any;
  hasTitleMinHeight?: boolean;
  text_in_image?: boolean;
  [key: string]: any;
}

export const Card: FC<CardProps> = forwardRef<
  HTMLAnchorElement & HTMLDivElement,
  CardProps
>(function CardFn(props: CardProps, ref) {
  const {
    link,
    title,
    description,
    image,
    percentage = 1,
    ar = 'ar_16_9',
    action,
    imageAspect,
    padding = true,
    backgroundAlt = false,
    backgroundColor,
    imageRef,
    notLink,
    moreBodySpace,
    video,
    hasTitleMinHeight,
    text_in_image,
    onClick,
    ...rest
  } = props;
  const router = useRouter();
  const { urlResolver } = useContext(PrismicContext);
  const url = Prismic.link(urlResolver, link);

  const onCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window?.getSelection?.()?.type === 'Range') {
      e.preventDefault();
      return false;
    }

    if (onClick) {
      onClick(e);
    }

    if (url.onClick) {
      return url.onClick(e);
    }

    return router.push(url.href ?? '', url.as);
  };

  const ImgBackground = styled(Img)`
    background-color: ${backgroundColor};
  `;

  const content = (
    <>
      {!!image &&
        (video ? (
          <ImageVideo
            image={image}
            ar={ar}
            aspectRatio={imageAspect}
            video={video}
          />
        ) : (
          <ImgBackground
            src={image}
            percentage={percentage}
            ar={ar}
            aspectRatio={imageAspect}
            ref={imageRef}
          />
        ))}
      <Body
        padding={padding}
        moreBodySpace={moreBodySpace}
        notLink={notLink}
        text_in_image={text_in_image}
      >
        {!!title && (
          <Title hasMinHeight={hasTitleMinHeight} text_in_image={text_in_image}>
            {title}
          </Title>
        )}
        {!!title && !!description && !text_in_image && <Divider />}
        {!!description && !text_in_image && <Copy>{description}</Copy>}
        {!!action && <CardAction padding={padding}>{action}</CardAction>}
      </Body>
    </>
  );

  if (notLink) {
    return (
      <CardHost backgroundAlt={backgroundAlt} {...rest}>
        {content}
      </CardHost>
    );
  }

  return (
    <CardHostLink
      backgroundAlt={backgroundAlt}
      ref={ref}
      {...rest}
      onClick={onCardClick}
    >
      {content}
    </CardHostLink>
  );
});
